import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PieChart as PieIcon } from "lucide-react";
import { Empty } from "../common";
import { eur, eur2 } from "../../lib/format";

const PALETTE = ["#f59e0b", "#38bdf8", "#34d399", "#f472b6", "#a78bfa", "#fb7185", "#facc15", "#2dd4bf", "#60a5fa", "#c084fc", "#fb923c", "#a3e635"];
const FIXED = { Manodopera: "#f59e0b", Mezzi: "#38bdf8" };
const TOOLTIP_STYLE = { background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 };
const BAR_MARGIN = { left: 8, right: 16 };
const BAR_CURSOR = { fill: "rgba(148,163,184,0.08)" };
const LEGEND_STYLE = { fontSize: 12 };

const colorMap = (names) => {
  const others = names.filter((n) => !FIXED[n]).sort((a, b) => a.localeCompare(b, "it"));
  return Object.fromEntries(names.map((n) => [n, FIXED[n] || PALETTE[(others.indexOf(n) + 2) % PALETTE.length]]));
};

const ChartCard = ({ title, testid, children }) => (
  <div className="surface p-4" data-testid={testid}>
    <div className="eyebrow mb-3 flex items-center gap-2"><PieIcon className="h-4 w-4 text-amber-400" />{title}</div>
    {children}
  </div>
);

export function SiteCostDonut({ byCat }) {
  const data = Object.entries(byCat || {}).map(([name, value]) => ({ name, value }));
  const colors = colorMap(data.map((d) => d.name));
  const total = data.reduce((a, d) => a + d.value, 0);
  if (!data.length) return <ChartCard title="Costi per categoria" testid="fin-cost-donut"><Empty title="Ancora nessun costo registrato" /></ChartCard>;
  return (
    <ChartCard title="Costi per categoria" testid="fin-cost-donut">
      <div className="grid md:grid-cols-[260px_minmax(0,460px)] gap-4 items-center">
        <div className="h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2} stroke="none">
                {data.map((d) => <Cell key={d.name} fill={colors[d.name]} />)}
              </Pie>
              <Tooltip formatter={(v) => eur2(v)} contentStyle={TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="space-y-1.5 text-sm">
          {data.map((d) => (
            <li key={d.name} className="flex items-center gap-2" data-testid={`fin-cost-cat-${d.name}`}>
              <span className="h-3 w-3 rounded-sm shrink-0" style={{ background: colors[d.name] }} />
              <span className="flex-1 truncate">{d.name}</span>
              <span className="num text-muted-foreground w-12 text-right">{total ? Math.round((d.value / total) * 100) : 0}%</span>
              <span className="num w-28 text-right">{eur2(d.value)}</span>
            </li>
          ))}
        </ul>
      </div>
    </ChartCard>
  );
}

export function SitesCostBars({ rows }) {
  const { data, cats, colors } = useMemo(() => {
    const names = [...new Set(rows.flatMap((r) => Object.keys(r.summary.costs_by_category || {})))];
    const chart = rows.filter((r) => r.summary.total_costs > 0)
      .map((r) => ({ site: r.site.code, label: `${r.site.code} · ${r.site.name}`, ...r.summary.costs_by_category }));
    return { data: chart, cats: names, colors: colorMap(names) };
  }, [rows]);
  if (!data.length) return <ChartCard title="Costi per cantiere e categoria" testid="finance-cost-bars"><Empty title="Ancora nessun costo registrato" /></ChartCard>;
  return (
    <ChartCard title="Costi per cantiere e categoria" testid="finance-cost-bars">
      <div style={{ height: Math.max(220, data.length * 46 + 90) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={BAR_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
            <XAxis type="number" tickFormatter={(v) => eur(v)} stroke="#94a3b8" fontSize={11} />
            <YAxis type="category" dataKey="site" width={80} stroke="#94a3b8" fontSize={11} />
            <Tooltip formatter={(v, n) => [eur2(v), n]} labelFormatter={(l, p) => p?.[0]?.payload?.label || l} contentStyle={TOOLTIP_STYLE} cursor={BAR_CURSOR} />
            <Legend wrapperStyle={LEGEND_STYLE} />
            {cats.map((c) => <Bar key={c} dataKey={c} stackId="costs" fill={colors[c]} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}
